import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Undo, Redo, Eraser } from 'lucide-react';


const CELL_SIZE = 20;

const TOOL_TYPES = {
  CORRIDOR: 'corridor',
  DOOR: 'door',
  FIRE_EXIT: 'fire_exit',
  WATER: 'water',
  ELECTRIC: 'electric',
  EMPTY: 'empty'
};

const TOOL_COLORS = {
  [TOOL_TYPES.CORRIDOR]: '#f3f4f6',
  [TOOL_TYPES.DOOR]: '#3b82f6',
  [TOOL_TYPES.FIRE_EXIT]: '#ef4444',
  [TOOL_TYPES.WATER]: '#06b6d4',
  [TOOL_TYPES.ELECTRIC]: '#f59e0b',
  [TOOL_TYPES.EMPTY]: '#ffffff'
};

const TOOL_LABELS = {
  [TOOL_TYPES.CORRIDOR]: 'Koridor',
  [TOOL_TYPES.DOOR]: 'Kapı',
  [TOOL_TYPES.FIRE_EXIT]: 'Yangın Çıkışı',
  [TOOL_TYPES.WATER]: 'Su',
  [TOOL_TYPES.ELECTRIC]: 'Elektrik',
  [TOOL_TYPES.EMPTY]: 'Temizle'
};

export default function GridEditor({ 
  gridWidth, 
  gridHeight, 
  gridData, 
  setGridData,
  zoom,
  onSave
}) {
  const [selectedTool, setSelectedTool] = useState(TOOL_TYPES.CORRIDOR);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Refs for stroke-based drag painting (avoids stale-closure issues)
  const strokeStartRef = useRef(null);   // snapshot before stroke
  const strokeGridRef  = useRef(null);   // live working grid during stroke

  // Initialise history when grid first loads (or when grid dimensions change externally)
  useEffect(() => {
    if (gridData.length > 0 && history.length === 0) {
      setHistory([JSON.parse(JSON.stringify(gridData))]);
      setHistoryIndex(0);
    }
  // eslint-disable-next-line
  }, [gridData.length]);

  // ── Stroke-based drag painting ────────────────────────────────────
  // Called on mousedown in grid mode
  const startStroke = (x, y) => {
    const snapshot = JSON.parse(JSON.stringify(gridData));
    // Paint the first cell immediately
    if (y >= 0 && y < gridHeight && x >= 0 && x < gridWidth && snapshot[y]?.[x] !== undefined) {
      snapshot[y][x] = { type: selectedTool, assignedTo: null };
    }
    strokeStartRef.current = JSON.parse(JSON.stringify(gridData)); // pre-stroke state
    strokeGridRef.current  = snapshot;
    setGridData(snapshot);
  };

  // Called on mousemove in grid mode while mouse button is held
  const paintStrokeCell = (x, y) => {
    if (!strokeGridRef.current) return;
    if (y < 0 || y >= gridHeight || x < 0 || x >= gridWidth) return;
    const current = strokeGridRef.current;
    if (!current[y]?.[x]) return;
    if (current[y][x].type === selectedTool) return; // already this type — skip

    const newGrid = current.map(row => row.map(cell => ({ ...cell })));
    newGrid[y][x] = { type: selectedTool, assignedTo: null };
    strokeGridRef.current = newGrid;
    setGridData(newGrid);
  };

  // Called on mouseup / mouseleave in grid mode — commits stroke to history
  const endStroke = () => {
    if (!strokeStartRef.current) return;
    const start = strokeStartRef.current;
    const final = strokeGridRef.current;
    strokeStartRef.current = null;
    strokeGridRef.current  = null;

    if (!final || JSON.stringify(final) === JSON.stringify(start)) return; // nothing changed

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(final)));
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    // gridData is already at `final` state from the last setGridData call in paintStrokeCell
  };

  const isPainting = () => strokeStartRef.current !== null;

  // ── Single-click cell update (kept for programmatic use) ──────────
  const updateCell = (x, y) => {
    const newGrid = JSON.parse(JSON.stringify(gridData));
    if (!newGrid[y]) return;
    newGrid[y][x] = { type: selectedTool, assignedTo: null };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newGrid)));
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setGridData(newGrid);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const idx = historyIndex - 1;
      setHistoryIndex(idx);
      setGridData(JSON.parse(JSON.stringify(history[idx])));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const idx = historyIndex + 1;
      setHistoryIndex(idx);
      setGridData(JSON.parse(JSON.stringify(history[idx])));
    }
  };

  const handleClearAll = () => {
    if (!window.confirm('Tüm çizimleri temizlemek istediğinize emin misiniz?')) return;
    
    const newGrid = [];
    for (let y = 0; y < gridHeight; y++) {
      const row = [];
      for (let x = 0; x < gridWidth; x++) {
        const isBoundary = x === 0 || x === gridWidth - 1 || y === 0 || y === gridHeight - 1;
        row.push({ 
          type: isBoundary ? TOOL_TYPES.CORRIDOR : TOOL_TYPES.EMPTY, 
          assignedTo: null 
        });
      }
      newGrid.push(row);
    }
    
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newGrid)));
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setGridData(newGrid);
  };

  return {
    selectedTool,
    setSelectedTool,
    isDrawing,
    setIsDrawing,
    updateCell,
    startStroke,
    paintStrokeCell,
    endStroke,
    isPainting,
    handleUndo,
    handleRedo,
    handleClearAll,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    TOOL_TYPES,
    TOOL_COLORS,
    TOOL_LABELS
  };
}
